"use client";

import {
  Badge,
  ListItem,
  ListItemAction,
  ListItemContent,
  ListItemIcon,
  ListItemText,
  Switch,
} from "@dafiti/closet-ui";
import { Chevron } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";

import { useLiveUpdate } from "../../../hooks/useLiveUpdate.hook";
import { useFiltersStore } from "../../../stores/filters.store";
import type { DefaultBooleanFacet, Facet } from "../../../types/filters.type";

function isValidIconUrl(url: string): boolean {
  return url.startsWith("https://");
}

type FilterListItemSwitchProps = {
  facet: DefaultBooleanFacet;
};

function FilterListItemSwitch({ facet }: FilterListItemSwitchProps) {
  const checked = useFiltersStore((s) => s.isPendingFilterActive(facet.field));
  const updatePendingFilter = useFiltersStore((s) => s.updatePendingFilter);
  const removePendingFilter = useFiltersStore((s) => s.removePendingFilter);
  const { triggerLiveUpdate } = useLiveUpdate();
  const hasIcon = Boolean(facet.icon && isValidIconUrl(facet.icon));

  const handleToggle = (value: boolean) => {
    if (value) {
      updatePendingFilter(facet.field as "enable_free_shipping", true);
    } else {
      removePendingFilter(facet.field, "");
    }
    triggerLiveUpdate();
  };

  return (
    <ListItem
      as="div"
      className={`w-full border-gray-200 cursor-pointer ${hasIcon ? "px-3xs py-nano" : "px-2xs py-nano"}`}
      onClick={() => handleToggle(!checked)}
      data-testid={`filter-list-item-switch-${facet.field}`}
    >
      {hasIcon && (
        <ListItemIcon>
          <img
            src={facet.icon!}
            alt=""
            className="size-3xs object-contain"
            aria-hidden="true"
          />
        </ListItemIcon>
      )}

      <ListItemContent>
        <ListItemText className="text-body-3 font-normal">
          {facet.label}
        </ListItemText>
      </ListItemContent>

      <ListItemAction className="ml-0" onClick={(e) => e.stopPropagation()}>
        <Switch
          className="-rotate-90 not-disabled:data-[state=checked]:bg-positive-200"
          checked={checked}
          onCheckedChange={handleToggle}
          aria-label={facet.label}
        />
      </ListItemAction>
    </ListItem>
  );
}

type FilterListItemDrillProps = {
  facet: Facet;
  onNavigate: (facet: Facet) => void;
  disableDrill?: boolean;
};

function FilterListItemDrill({
  facet,
  onNavigate,
  disableDrill = false,
}: FilterListItemDrillProps) {
  const t = useTranslations("catalog");

  return (
    <ListItem
      as="button"
      className="w-full border-gray-200 px-2xs py-nano disabled:cursor-default disabled:bg-transparent disabled:text-inherit cursor-pointer"
      onClick={() => onNavigate(facet)}
      disabled={disableDrill}
      data-testid={`filter-list-item-drill-${facet.field}`}
      aria-label={t("facet.filterBy", { label: facet.label })}
    >
      <ListItemContent>
        <ListItemText className="flex items-center gap-3xs text-body-3 lg:text-body-2 font-normal">
          {facet.label}
          {facet.is_selected ? (
            <Badge
              className="bg-support-200"
              size="md"
              aria-label={t("facet.activeFilter")}
              data-testid={`filter-badge-${facet.field}`}
            />
          ) : null}
        </ListItemText>
      </ListItemContent>

      <ListItemAction className="ml-0">
        {!disableDrill && <Chevron className="size-3xs" aria-hidden="true" />}
      </ListItemAction>
    </ListItem>
  );
}

export { FilterListItemSwitch, FilterListItemDrill };
export type { FilterListItemSwitchProps, FilterListItemDrillProps };
